import { useState } from 'react';
import { skipToken, useMutation, useQuery } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/language-context';
import { useCurrentBusiness } from '@/hooks/use-current-business';
import { useEntitlements } from '@/hooks/use-entitlements';
import { businessQueryKey, getBusiness } from '@/lib/api/businesses';
import {
  createPaymentMethod,
  listPaymentMethods,
  paymentMethodsQueryKey,
  updatePaymentMethod,
  type PaymentMethodDto,
} from '@/lib/api/payment-methods';

import { CashLogo, CheckLogo, StripeLogo, ZelleLogo, VenmoLogo } from './logos';
import { MethodPickerModal } from './method-picker-modal';
import { PaymentMethodFormModal } from './payment-method-form-modal';
import { PaymentTile } from './payment-tile';
import { StripeConnectCard } from './stripe-connect-card';
import type { MethodType, ModalState } from './types';

const LOGOS: Record<MethodType, React.FC<{ className?: string }>> = {
  zelle: ZelleLogo,
  venmo: VenmoLogo,
  cash: CashLogo,
  check: CheckLogo,
};

function methodTitle(method: PaymentMethodDto, t: ReturnType<typeof useLanguage>['t']): string {
  switch (method.type) {
    case 'zelle':
      return t.zelleTitle;
    case 'venmo':
      return t.venmoTitle;
    case 'cash':
      return t.cashTitle;
    case 'check':
      return t.checkTitle;
    default:
      return method.type;
  }
}

function methodSubtitle(method: PaymentMethodDto): string {
  const config = method.config as Record<string, unknown>;
  const detail = config.phone ?? config.handle;
  const parts = [method.label, typeof detail === 'string' ? detail : null].filter(Boolean);
  return parts.length > 0 ? parts.join(' — ') : '';
}

export function Payments() {
  const { t } = useLanguage();
  const business = useCurrentBusiness();
  const businessId = business?.business_id;
  const { has } = useEntitlements();

  const businessQuery = useQuery({
    queryKey: businessQueryKey(businessId ?? ''),
    queryFn: businessId ? () => getBusiness(businessId) : skipToken,
  });

  const methodsQuery = useQuery({
    queryKey: paymentMethodsQueryKey(businessId ?? ''),
    queryFn: businessId ? () => listPaymentMethods(businessId) : skipToken,
  });

  const createMutation = useMutation({
    mutationFn: (body: { type: Exclude<MethodType, 'stripe'>; label: string; config: Record<string, unknown> }) =>
      createPaymentMethod(businessId!, body),
    meta: { successMessage: t.paymentMethodCreated },
    onSuccess: () => setModal(null),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      label?: string;
      config?: Record<string, unknown>;
      is_active?: boolean;
    }) => updatePaymentMethod(businessId!, id, body),
    meta: { successMessage: t.paymentMethodUpdated },
    onSuccess: () => setModal(null),
  });

  const [modal, setModal] = useState<ModalState | null>(null);

  const methods = methodsQuery.data ?? [];
  const active = methods.filter((m) => m.is_active);
  const inactive = methods.filter((m) => !m.is_active);

  const editingMethod =
    modal?.type === 'edit' ? methods.find((m) => m.id === modal.id) : undefined;

  function handleCreate(data: { label: string; config: Record<string, unknown>; is_active: boolean }) {
    if (modal?.type !== 'create') return;
    createMutation.mutate({ type: modal.methodType, label: data.label, config: data.config });
  }

  function handleUpdate(data: { label: string; config: Record<string, unknown>; is_active: boolean }) {
    if (!editingMethod) return;
    updateMutation.mutate({ id: editingMethod.id, ...data });
  }

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{t.paymentMethodsTitle}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t.paymentMethodsSubtitle}</p>
        </div>
        <Button
          size="sm"
          onClick={() => setModal({ type: 'picker' })}
          className="gap-1.5 shrink-0"
          data-testid="add-payment-method"
        >
          <PlusIcon className="w-3.5 h-3.5" />
          {t.addPaymentMethod}
        </Button>
      </div>

      <div className="flex flex-col gap-3">
        {businessQuery.data && has('stripe_payments') && (
          <StripeConnectCard
            businessId={businessQuery.data.id}
            chargesEnabled={businessQuery.data.stripe_connect_charges_enabled}
            hasStartedOnboarding={Boolean(businessQuery.data.stripe_connect_account_id)}
          />
        )}

        {active.map((method) => (
          <PaymentTile
            key={method.id}
            Logo={LOGOS[method.type as MethodType] ?? StripeLogo}
            title={methodTitle(method, t)}
            subtitle={methodSubtitle(method)}
            isConfigured
            onConfigure={() => setModal({ type: 'edit', id: method.id })}
            onEdit={() => setModal({ type: 'edit', id: method.id })}
            onRemove={() => updateMutation.mutate({ id: method.id, is_active: false })}
          />
        ))}

        {inactive.length > 0 && (
          <div className="flex flex-col gap-3 pt-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t.paymentMethodInactive}
            </p>
            {inactive.map((method) => (
              <PaymentTile
                key={method.id}
                Logo={LOGOS[method.type as MethodType] ?? StripeLogo}
                title={methodTitle(method, t)}
                subtitle={methodSubtitle(method)}
                isConfigured
                onConfigure={() => setModal({ type: 'edit', id: method.id })}
                onEdit={() => setModal({ type: 'edit', id: method.id })}
              />
            ))}
          </div>
        )}
      </div>

      {modal?.type === 'picker' && (
        <MethodPickerModal
          onSelect={(methodType) => setModal({ type: 'create', methodType })}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'create' && (
        <PaymentMethodFormModal
          methodType={modal.methodType}
          onSave={handleCreate}
          onClose={() => setModal(null)}
          isSaving={createMutation.isPending}
        />
      )}
      {modal?.type === 'edit' && editingMethod && (
        <PaymentMethodFormModal
          methodType={editingMethod.type as MethodType}
          initial={editingMethod}
          onSave={handleUpdate}
          onClose={() => setModal(null)}
          isSaving={updateMutation.isPending}
        />
      )}
    </div>
  );
}
